import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'recharts';
import { useDealInsightsContext } from '../../../../context/DealInsightsContext';
import { type DateResolution, DateResolutionToggle } from '../../../DateResolutionToggle/DateResolutionToggle';
import { MOCK_BID_RATE_WIN_RATE_DATA } from '../chartUtils';
import { RateComparisonChartCore } from './components/RateComparisonChartCore';
import { RateComparsionTooltip } from './components/RateComparsionTooltip';

type RateComparsionChartProps = {
  hideHeader?: boolean;
};

export const RateComparsionChart = ({ hideHeader = false }: RateComparsionChartProps = {}) => {
  const { t } = useTranslation();
  const {
    dealInsights: { data, chartsLoadingState },
    chartDateResolutions,
    updateChartDateResolution,
  } = useDealInsightsContext();

  const dateResolution = chartDateResolutions.bidrate_winrate;
  const setDateResolution = (resolution: DateResolution) => updateChartDateResolution('bidrate_winrate', resolution);

  const isLoading = !!chartsLoadingState.bidrate_winrate;
  const values = isLoading ? MOCK_BID_RATE_WIN_RATE_DATA : (data?.bidrate_winrate ?? []);

  const shouldRenderChart = values.length > 0;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: hideHeader ? 0 : 4, height: 420 }}
      data-test="rate-comparsion-chart-container"
    >
      {!hideHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h2" data-test="rate-comparsion-chart-title">
            {t('Bid Rate vs Win Rate')}
          </Typography>
          <DateResolutionToggle value={dateResolution} onChange={setDateResolution} disabled={isLoading} />
        </Box>
      )}

      {/* Relative and absolute wrappers are needed for chart to be responsive */}
      <Box sx={{ position: 'relative', width: '100%', height: 280 }}>
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: 0 }}>
          {shouldRenderChart && (
            <RateComparisonChartCore
              values={values}
              isLoading={isLoading}
              showLegend={!hideHeader}
              showTooltip={!isLoading}
              tooltipContent={<Tooltip content={<RateComparsionTooltip />} />}
            />
          )}

          {!shouldRenderChart && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                gap: 0.5,
              }}
            >
              <Typography variant="body1Bold">{t('No data')}</Typography>
              <Typography variant="body1" color={palette.text.secondary}>
                {t('Try adjusting filters or date range to see the results.')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
